import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Upload,
  Layers,
  Wand2,
  Droplets,
  Contrast,
  Sparkles,
  AlertTriangle,
  Download,
  Image as ImageIcon,
  Eye,
  Loader2,
  FileImage,
} from "lucide-react";
import {
  DEFAULT_WHITE_LAYER_SETTINGS,
  WhiteLayerExportFormat,
  WhiteLayerPreviewMode,
  WhiteLayerSettings,
} from "../types/whiteLayer";
import { useWhiteLayerPreview } from "../hooks/useWhiteLayerPreview";
import { useWhiteLayerExport } from "../hooks/useWhiteLayerExport";
import { buildStats } from "../utils/white-layer/buildStats";
import { buildWarnings } from "../utils/white-layer/buildWarnings";
import { buildPreviewMask } from "../utils/white-layer/buildPreviewMask";

type MessageTone = "info" | "warning" | "danger" | "success";

export default function WhiteLayerToolPage() {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageName, setImageName] = useState<string>("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewMode, setPreviewMode] =
    useState<WhiteLayerPreviewMode>("overlay");
  const [exportFormat, setExportFormat] =
    useState<WhiteLayerExportFormat>("png");
  const [settings, setSettings] = useState<WhiteLayerSettings>(
    DEFAULT_WHITE_LAYER_SETTINGS
  );
  const [exportMessage, setExportMessage] = useState<string | null>(null);
  const [exportMessageTone, setExportMessageTone] =
    useState<MessageTone>("info");

  const { previewUrl, loading } = useWhiteLayerPreview({
    imageUrl,
    settings,
    mode: previewMode,
  });

  const {
    exportWhiteLayer,
    isExporting,
    exportError,
    clearExportError,
  } = useWhiteLayerExport();

  const stats = useMemo(() => buildStats(settings), [settings]);
  const warnings = useMemo(() => buildWarnings(settings), [settings]);

  const hasArtwork = Boolean(uploadedFile && imageUrl);
  const canExport = hasArtwork && !isExporting;

  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  const handleFileChange = (file: File | null) => {
    if (!file) return;

    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }

    const nextUrl = URL.createObjectURL(file);

    setUploadedFile(file);
    setImageUrl(nextUrl);
    setImageName(file.name);
    setPreviewMode("overlay");
    setExportMessage(null);
    setExportMessageTone("info");
    clearExportError();
  };

  const handleReset = () => {
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }

    setUploadedFile(null);
    setImageUrl(null);
    setImageName("");
    setPreviewMode("overlay");
    setExportFormat("png");
    setSettings(DEFAULT_WHITE_LAYER_SETTINGS);
    setExportMessage(null);
    setExportMessageTone("info");
    clearExportError();
  };

  const handleExport = async () => {
    setExportMessage(null);
    clearExportError();

    if (!uploadedFile || !imageUrl) {
      setExportMessageTone("warning");
      setExportMessage("Upload an image before exporting the white layer.");
      return;
    }

    try {
      const payload = await buildProcessedPayload({
        imageUrl,
        settings,
        fileName: uploadedFile.name,
        exportFormat,
      });

      const result = await exportWhiteLayer(payload);

      if (result.success) {
        setExportMessageTone("success");
        setExportMessage(
          result.fileName ||
            (exportFormat === "tiff"
              ? "White layer TIFF exported successfully."
              : "White layer PNG exported successfully.")
        );
        return;
      }

      setExportMessageTone("danger");
      setExportMessage(
        result.error || "Export failed while writing white layer."
      );
    } catch (error) {
      console.error("White layer export failed:", error);

      const message =
        error instanceof Error
          ? error.message
          : "Unexpected error while preparing white layer export.";

      setExportMessageTone("danger");
      setExportMessage(message);
    }
  };

  const previewCardTitle =
    previewMode === "original"
      ? "Original Artwork"
      : previewMode === "w1"
        ? "W1 Spot Preview"
        : "Overlay Preview";

  const previewCardDescription =
    previewMode === "original"
      ? "Your uploaded artwork preview."
      : previewMode === "w1"
        ? "Live grayscale preview of the first-pass W1 support layer."
        : "Live browser preview using your current white layer settings.";

  const exportButtonLabel = hasArtwork
    ? exportFormat === "tiff"
      ? "Export CMYK + W1 TIFF"
      : "Export W1 PNG"
    : "Upload Image First";

  return (
    <>
      <Helmet>
        <title>White Layer Tool | DTF Wizard</title>
        <meta
          name="description"
          content="Preview and tune a DTF white support layer with controls for white strength, edge tightness, dark reduction, fade support, and noise cleanup."
        />
      </Helmet>

      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
        <section className="overflow-hidden rounded-3xl border border-cyan-500/20 bg-[linear-gradient(135deg,rgba(6,15,31,0.96),rgba(8,26,52,0.96))] shadow-[0_0_40px_rgba(34,211,238,0.12)]">
          <div className="flex flex-col gap-4 p-6 sm:p-8">
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-cyan-200">
              <Layers className="h-4 w-4" />
              White Layer Tool
            </div>

            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-3xl space-y-3">
                <h1 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                  Build and preview your W1 support layer before export
                </h1>
                <p className="max-w-2xl text-sm leading-6 text-slate-300 sm:text-base">
                  Upload one image, adjust your support settings, preview the
                  live result, and export the generated white layer directly from
                  your browser.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <StatChip label="Upload Mode" value="Single Image" />
                <StatChip label="Preview" value={previewMode.toUpperCase()} />
                <StatChip
                  label="Coverage"
                  value={`${stats.estimatedCoverage}%`}
                />
                <StatChip label="Print Feel" value={stats.estimatedFeel} />
              </div>
            </div>
          </div>
        </section>

        <div className="grid gap-6 xl:grid-cols-[380px_minmax(0,1fr)]">
          <section className="flex flex-col gap-6">
            <Card>
              <CardHeader
                icon={<Upload className="h-5 w-5" />}
                title="Artwork Upload"
                description="Upload a transparent PNG to start building your white support preview."
              />

              <div className="space-y-4">
                <label className="group flex cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed border-cyan-400/25 bg-white/[0.03] px-4 py-8 text-center transition hover:border-cyan-300/45 hover:bg-cyan-300/[0.05]">
                  <Upload className="mb-3 h-8 w-8 text-cyan-300 transition group-hover:scale-105" />
                  <div className="text-sm font-semibold text-white">
                    Tap to upload artwork
                  </div>
                  <div className="mt-1 text-xs text-slate-400">
                    Transparent PNG recommended for best W1 preview behavior
                  </div>
                  <input
                    type="file"
                    accept="image/png,image/webp,image/jpeg"
                    className="hidden"
                    onChange={(event) =>
                      handleFileChange(event.target.files?.[0] ?? null)
                    }
                  />
                </label>

                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                    Current file
                  </div>
                  <div className="mt-2 text-sm text-white">
                    {imageName || "No artwork uploaded yet"}
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="inline-flex items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm font-semibold text-slate-200 transition hover:bg-white/[0.08]"
                  >
                    Reset Tool
                  </button>

                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-4 py-3 text-sm font-semibold text-cyan-100 transition hover:bg-cyan-400/20"
                  >
                    Send to Gangsheet Builder
                  </button>
                </div>
              </div>
            </Card>

            <Card>
              <CardHeader
                icon={<Wand2 className="h-5 w-5" />}
                title="White Layer Controls"
                description="These controls drive the live browser preview and export payload."
              />

              <div className="space-y-5">
                <RangeRow
                  label="White Strength"
                  value={settings.whiteStrength}
                  min={0}
                  max={100}
                  icon={<Droplets className="h-4 w-4" />}
                  help="Overall amount of white support generated."
                  onChange={(value) =>
                    setSettings((prev) => ({ ...prev, whiteStrength: value }))
                  }
                />

                <RangeRow
                  label="Edge Tightness"
                  value={settings.edgeTightness}
                  min={0}
                  max={8}
                  step={1}
                  icon={<Contrast className="h-4 w-4" />}
                  help="Higher values tighten the support edge."
                  onChange={(value) =>
                    setSettings((prev) => ({ ...prev, edgeTightness: value }))
                  }
                />

                <RangeRow
                  label="Reduce White on Dark Colors"
                  value={settings.darkReduction}
                  min={0}
                  max={100}
                  icon={<Sparkles className="h-4 w-4" />}
                  help="Backs white down under darker colors to help reduce stiffness."
                  onChange={(value) =>
                    setSettings((prev) => ({ ...prev, darkReduction: value }))
                  }
                />

                <RangeRow
                  label="Fade Support"
                  value={settings.fadeSupport}
                  min={0}
                  max={100}
                  icon={<Droplets className="h-4 w-4" />}
                  help="Helps keep soft fades and semi-transparent areas printable."
                  onChange={(value) =>
                    setSettings((prev) => ({ ...prev, fadeSupport: value }))
                  }
                />

                <RangeRow
                  label="Clean Up Noise"
                  value={settings.noiseCleanup}
                  min={0}
                  max={30}
                  step={1}
                  icon={<Sparkles className="h-4 w-4" />}
                  help="Removes weak junk pixels from the white layer."
                  onChange={(value) =>
                    setSettings((prev) => ({ ...prev, noiseCleanup: value }))
                  }
                />
              </div>
            </Card>

            <Card>
              <CardHeader
                icon={<AlertTriangle className="h-5 w-5" />}
                title="Preview Warnings"
                description="These warnings come from shared white-layer logic so the page and export flow stay aligned."
              />

              <div className="space-y-3">
                {warnings.map((warning) => (
                  <WarningCard
                    key={warning.id}
                    title={warning.title}
                    message={warning.message}
                    level={warning.level}
                  />
                ))}
              </div>
            </Card>
          </section>

          <section className="flex flex-col gap-6">
            <Card>
              <div className="flex flex-col gap-4 border-b border-white/10 pb-5 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="flex items-center gap-2 text-sm font-semibold text-white">
                    <Eye className="h-4 w-4 text-cyan-300" />
                    {previewCardTitle}
                  </div>
                  <p className="mt-1 text-sm text-slate-400">
                    {previewCardDescription}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <PreviewTab
                    active={previewMode === "original"}
                    label="Original"
                    onClick={() => setPreviewMode("original")}
                  />
                  <PreviewTab
                    active={previewMode === "w1"}
                    label="W1 Only"
                    onClick={() => setPreviewMode("w1")}
                  />
                  <PreviewTab
                    active={previewMode === "overlay"}
                    label="Overlay"
                    onClick={() => setPreviewMode("overlay")}
                  />
                </div>
              </div>

              <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
                <div className="overflow-hidden rounded-3xl border border-white/10 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.08),rgba(255,255,255,0.02),rgba(255,255,255,0.02))] p-4">
                  <div className="relative flex min-h-[420px] items-center justify-center rounded-[1.6rem] border border-dashed border-white/10 bg-[#081120] p-4">
                    {(loading || isExporting) && imageUrl ? (
                      <div className="absolute inset-0 z-20 flex items-center justify-center rounded-[1.6rem] bg-[#081120]/82 backdrop-blur-[2px]">
                        <div className="flex items-center gap-3 rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-4 py-3 text-sm font-semibold text-cyan-100">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {isExporting
                            ? exportFormat === "tiff"
                              ? "Building CMYK + W1 TIFF..."
                              : "Processing white layer PNG..."
                            : "Building preview..."}
                        </div>
                      </div>
                    ) : null}

                    {previewUrl ? (
                      <div className="relative inline-flex max-h-[520px] max-w-full items-center justify-center">
                        <img
                          src={previewUrl}
                          alt="White layer preview"
                          className="relative z-10 max-h-[500px] max-w-full object-contain"
                        />
                      </div>
                    ) : imageUrl ? (
                      <div className="flex max-w-md flex-col items-center text-center">
                        <div className="mb-4 rounded-2xl border border-cyan-400/20 bg-cyan-400/10 p-4">
                          <Loader2 className="h-10 w-10 animate-spin text-cyan-200" />
                        </div>
                        <h3 className="text-lg font-bold text-white">
                          Preparing preview
                        </h3>
                        <p className="mt-2 text-sm leading-6 text-slate-400">
                          Your uploaded file is loading into the live preview
                          engine.
                        </p>
                      </div>
                    ) : (
                      <div className="flex max-w-md flex-col items-center text-center">
                        <div className="mb-4 rounded-2xl border border-cyan-400/20 bg-cyan-400/10 p-4">
                          <ImageIcon className="h-10 w-10 text-cyan-200" />
                        </div>
                        <h3 className="text-lg font-bold text-white">
                          No artwork loaded yet
                        </h3>
                        <p className="mt-2 text-sm leading-6 text-slate-400">
                          Upload a file on the left to start previewing the white
                          support layer workflow for this page.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <InfoTile
                    label="Estimated W1 Coverage"
                    value={`${stats.estimatedCoverage}%`}
                    description="A rough first-pass estimate based on your current tuning values."
                  />
                  <InfoTile
                    label="Estimated Density"
                    value={`${stats.estimatedDensity}%`}
                    description="Higher values suggest a stronger or heavier white support build."
                  />
                  <InfoTile
                    label="Estimated Print Feel"
                    value={stats.estimatedFeel}
                    description="This gives a fast idea of whether the print may feel light, balanced, or heavy."
                  />
                  <InfoTile
                    label="Halo Risk"
                    value={stats.haloRisk}
                    description="This reacts to your current edge tightness settings."
                  />
                </div>
              </div>
            </Card>

            <Card>
              <CardHeader
                icon={<Download className="h-5 w-5" />}
                title="Export"
                description="This export runs fully in the browser and downloads the generated white layer as PNG or RGB artwork + W1 alpha TIFF."
              />

              <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_260px]">
                <div className="space-y-4">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm leading-6 text-slate-300">
                    The W1 mask is built locally in your browser from the same
                    white-layer preview logic you see on screen.
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                      Export format
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <FormatButton
                        active={exportFormat === "png"}
                        label="PNG"
                        icon={<ImageIcon className="h-4 w-4" />}
                        onClick={() => setExportFormat("png")}
                      />
                      <FormatButton
                        active={exportFormat === "tiff"}
                        label="TIFF"
                        icon={<FileImage className="h-4 w-4" />}
                        onClick={() => setExportFormat("tiff")}
                      />
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                      Export readiness
                    </div>
                    <div className="mt-2 text-sm font-semibold text-white">
                      {hasArtwork
                        ? `Artwork loaded. ${exportFormat.toUpperCase()} export is ready.`
                        : "Upload artwork to enable white layer export."}
                    </div>
                  </div>

                  {exportMessage ? (
                    <WarningCard
                      title="Export Status"
                      message={exportMessage}
                      level={exportMessageTone}
                    />
                  ) : null}

                  {exportError ? (
                    <WarningCard
                      title="Export Error"
                      message={exportError}
                      level="danger"
                    />
                  ) : null}
                </div>

                <div className="relative z-30">
                  <button
                    type="button"
                    onClick={handleExport}
                    disabled={!canExport}
                    className={`inline-flex min-h-[120px] w-full items-center justify-center rounded-3xl border px-5 py-4 text-center text-sm font-bold shadow-[0_0_25px_rgba(34,211,238,0.08)] transition ${
                      canExport
                        ? "cursor-pointer border-cyan-400/20 bg-[linear-gradient(135deg,rgba(34,211,238,0.18),rgba(59,130,246,0.12))] text-cyan-50 hover:scale-[1.01] hover:bg-[linear-gradient(135deg,rgba(34,211,238,0.24),rgba(59,130,246,0.16))]"
                        : "cursor-not-allowed border-white/10 bg-white/[0.04] text-slate-500 opacity-80"
                    }`}
                  >
                    {isExporting ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Processing...
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        {hasArtwork ? (
                          <>
                            <Download className="h-4 w-4" />
                            {exportButtonLabel}
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4" />
                            Upload Image First
                          </>
                        )}
                      </span>
                    )}
                  </button>

                  {isExporting ? (
                    <div className="mt-4 w-full">
                      <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
                        <div className="h-full w-[55%] animate-pulse rounded-full bg-cyan-400" />
                      </div>
                      <div className="mt-2 text-center text-xs text-cyan-300">
                        {exportFormat === "tiff"
                          ? "Building CMYK + W1 TIFF..."
                          : "Building white layer PNG..."}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </Card>
          </section>
        </div>
      </div>
    </>
  );
}

async function buildProcessedPayload({
  imageUrl,
  settings,
  fileName,
  exportFormat,
}: {
  imageUrl: string;
  settings: WhiteLayerSettings;
  fileName: string;
  exportFormat: WhiteLayerExportFormat;
}) {
  const img = await loadImage(imageUrl);

  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;

  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) {
    throw new Error("Could not create export canvas context.");
  }

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);

  const original = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const originalBase64 = canvas.toDataURL("image/png").split(",")[1];

  if (!originalBase64) {
    throw new Error("Failed to encode original image for export.");
  }

  const mask = buildPreviewMask(original, settings);
  const flattenedMask = flattenMask(mask.data);

  return {
    sourceType: "processed-image" as const,
    target: "both" as const,
    width: canvas.width,
    height: canvas.height,
    originalImageBase64: originalBase64,
    w1MaskBase64: uint8ToBase64(flattenedMask),
    fileName,
    mimeType: exportFormat === "tiff" ? "image/tiff" : "image/png",
    exportFormat,
  };
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image for export."));
    img.src = src;
  });
}

function flattenMask(maskRgba: Uint8ClampedArray): Uint8Array {
  const out = new Uint8Array(maskRgba.length / 4);

  for (let i = 0; i < maskRgba.length; i += 4) {
    out[i / 4] = maskRgba[i];
  }

  return out;
}

function uint8ToBase64(buffer: Uint8Array | Uint8ClampedArray): string {
  let binary = "";
  const chunkSize = 8192;

  for (let i = 0; i < buffer.length; i += chunkSize) {
    const chunk = buffer.subarray(i, i + chunkSize);
    let chunkBinary = "";
    for (let j = 0; j < chunk.length; j += 1) {
      chunkBinary += String.fromCharCode(chunk[j]);
    }
    binary += chunkBinary;
  }

  return btoa(binary);
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <section className="rounded-3xl border border-white/10 bg-[rgba(7,15,30,0.88)] p-5 shadow-[0_16px_50px_rgba(0,0,0,0.22)] backdrop-blur-sm sm:p-6">
      {children}
    </section>
  );
}

function CardHeader({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="mb-5 border-b border-white/10 pb-5">
      <div className="flex items-center gap-2 text-sm font-semibold text-white">
        <span className="text-cyan-300">{icon}</span>
        {title}
      </div>
      <p className="mt-2 text-sm leading-6 text-slate-400">{description}</p>
    </div>
  );
}

function StatChip({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
        {label}
      </div>
      <div className="mt-1 text-sm font-bold text-white">{value}</div>
    </div>
  );
}

function PreviewTab({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-2xl border px-4 py-2 text-sm font-semibold transition ${
        active
          ? "border-cyan-400/30 bg-cyan-400/15 text-cyan-100"
          : "border-white/10 bg-white/[0.04] text-slate-300 hover:bg-white/[0.08]"
      }`}
    >
      {label}
    </button>
  );
}

function FormatButton({
  active,
  label,
  icon,
  onClick,
}: {
  active: boolean;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-semibold transition ${
        active
          ? "border-cyan-400/30 bg-cyan-400/15 text-cyan-100"
          : "border-white/10 bg-white/[0.04] text-slate-300 hover:bg-white/[0.08]"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function InfoTile({
  label,
  value,
  description,
}: {
  label: string;
  value: string;
  description: string;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
        {label}
      </div>
      <div className="mt-2 text-lg font-extrabold text-white">{value}</div>
      <p className="mt-2 text-sm leading-6 text-slate-400">{description}</p>
    </div>
  );
}

function WarningCard({
  title,
  message,
  level,
}: {
  title: string;
  message: string;
  level: "info" | "warning" | "danger" | "success";
}) {
  const toneMap = {
    info: "border-cyan-400/20 bg-cyan-400/10 text-cyan-100",
    warning: "border-amber-400/20 bg-amber-400/10 text-amber-100",
    danger: "border-rose-400/20 bg-rose-400/10 text-rose-100",
    success: "border-emerald-400/20 bg-emerald-400/10 text-emerald-100",
  } as const;

  return (
    <div className={`rounded-2xl border p-4 ${toneMap[level]}`}>
      <div className="text-sm font-semibold">{title}</div>
      <div className="mt-1 text-sm leading-6 opacity-95">{message}</div>
    </div>
  );
}

function RangeRow({
  label,
  value,
  min,
  max,
  step = 1,
  icon,
  help,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  icon: React.ReactNode;
  help: string;
  onChange: (value: number) => void;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-semibold text-white">
            <span className="text-cyan-300">{icon}</span>
            {label}
          </div>
          <p className="mt-1 text-xs leading-5 text-slate-400">{help}</p>
        </div>

        <div className="rounded-xl border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-sm font-bold text-cyan-100">
          {value}
        </div>
      </div>

      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className="mt-4 h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-700 accent-cyan-400"
      />

      <div className="mt-2 flex justify-between text-[11px] uppercase tracking-[0.15em] text-slate-500">
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );
}